import { DxDataGridComponent, DxPivotGridComponent } from "devextreme-angular";

/*
A. TYPE DEFINITIONS
*/

export class Column {
    dataField?: string;
    caption?: string;
    width?: string;
    isBand?: boolean;
    ownerBand?: number;  
    visible?: boolean;
    alignment?: "center"|"left"|"right";
    customizeText?: (cellInfo: any) => string;
    format?: Object;
    formatString?: string;
    cssClass?: string;
    encodeHtml?: boolean;    
    dataType?: any
}

export class ColumnDefinition {
    datagridName: string;
    staticColumnDefs: Array<Column>;
    dynamicColsFunc?: Function;
    customizeColsFunc?: Function;
}

export class DataGridCreatedEvent {
    reportType: string;
    dataGridComponent: any;
    columnFormatDetails?: Array<Object>;
    totalRowCount?: number;
}

export const excelCustomFormats = [
    {gridFmt: 'enUS_currencyRounded', excelFmt: '[$-en-US,1]$#,##0;($#,##0)'},
    {gridFmt: 'enUS_roundToWholeNumber', excelFmt: '[$-en-US,1]#,##0;(#,##0)'},
    {gridFmt: 'enUS_roundToTwoDecimals', excelFmt: '[$-en-US,1]0.00;(0.00)'},
    {gridFmt: 'roundToWholeNumber', excelFmt: '[$-en-US,1]#'},
  ];


export const requiresDecompression = [
    "LeadsWith91MA_AllLeadsDetails",
    "Oppty_OpenOpptyDetails",
    "OpptysWith91MA_AllOpptysDetails",
    "CRM_Prospects_With91MA",
    "CRM_Suspects",
]

//********** END *******************

/*
B. CUSTOM FORMATTING FUNCTIONS: 
*/

let default_to_nodata = (cellInfo: any) => cellInfo.value? cellInfo.value : '<no data>';

let get_date_from_isoString = (cellInfo: any) => cellInfo.value? cellInfo.value.slice(0,10): cellInfo.value;

let get_yrmonth_from_isostring = (cellInfo: any) => cellInfo.value? cellInfo.value.slice(0,7): cellInfo.value;

//Format string "N2_KG" as "N2 \n (KG)", to display in header band
export const customize_lbsap_band_header = (hdrName: string) => {
    let t = hdrName.split('_');
    return "".concat(t[0],'\r\n','(',t[1],')');
};

let enUS_currencyRounded = { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }

//use this formatter for currency rounding. for negative values eg; -11, it formats as (11).
let enUS_roundToWholeNumber = { maximumFractionDigits: 0 }

//use this formatter for normal number rounding. It retains the minus sign for negative values.
let roundToWholeNumber = { maximumFractionDigits: 0 }

let enUS_roundToTwoDecimals = { minimumFractionDigits: 2, maximumFractionDigits: 2 }

let generate_leadID_hyperlink = (cellInfo: any) => {

    return `<a href="https://crm.america.apci.com:9080/sap/crm_logon/default.htm?crm-object-type=BT108_LEA&crm-object-action=B
&crm-object-value=${cellInfo.value}&crm-object-keyname=OBJECT_ID+" target="_blank">${cellInfo.value}</a>`;

}

let generate_OpptyID_hyperlink = (cellInfo: any) => {

    return `<a href="https://crm.america.apci.com:9080/sap/bc/bsp/sap/crm_ui_start/default.htm?crm-object-type=BT111_OPPT
&crm-object-action=B&crm-object-value=${cellInfo.value}&crm-object-keyname=OBJECT_ID+" target="_blank">${cellInfo.value}</a>`;

}

let generate_acctnum_hyperlink = (cellInfo: any) => {
    return `<a href="https://crm.america.apci.com:9080/sap/crm_logon/default.htm?crm-object-type=BP_ACCOUNT&crm-object-action=B
&crm-object-keyname=PARTNER&crm-object-value=${cellInfo.value}" target="_blank">${cellInfo.value}</a>`;    
}

let yesNoValues_resetNoToEmpty = (cellInfo: any) => {
    return cellInfo.value == 'Yes'? cellInfo.value : '';
}

let boolToStringUpper = (cellInfo: any) => {
    return cellInfo.value? 'TRUE' : 'FALSE'
}

//********** END *******************

/*
C. STATIC COLUMN DEFINITIONS: 
*/

export const column_definitions: Array<ColumnDefinition> = [
    {
        datagridName: "CurrCust_ALLSAP",
        staticColumnDefs: [
            {dataField: "territory", caption: "Territory by Zip"},
            {dataField: "shipToPartyId", caption: "ShipToID"},
            {dataField: "priSalesPosCRM", caption: "PrimarySalesPos CRM"},
            {dataField: "shipToPrimarySalesPosRepClassification", caption: "ShipToPrimary SalesPos AcctType"},
            {dataField: "profitCenter", caption: "SAP Profit Center"},
            {dataField: "dollarsPerYr", caption: "$/Yr", format: enUS_currencyRounded, formatString: "enUS_currencyRounded"},
            {dataField: "bemfabDesc", caption: "BEMFAB from D&B"},
            {dataField: "dunsNumber", caption: "DUNS Number"},
            {dataField: "shipToParty", caption: "Ship To Party"},
            {dataField: "streetName", caption: "Street"},
            {dataField: "locationCity", caption: "City"},
            {dataField: "countyPrefecture", caption: "County"},
            {dataField: "province", caption: "State"},
            {dataField: "postalCode", caption: "Postal Code"},
            {dataField: "isCryoEase", caption: "is Cryo Ease"},
            {dataField: "contractEndDate", caption: "Contract End Date", customizeText: get_date_from_isoString},
            {dataField: "firstOrderMonth", caption: "First Order", customizeText: get_yrmonth_from_isostring},
            {dataField: "lastOrderMonth", caption: "Last Order", customizeText: get_yrmonth_from_isostring},
            {dataField: "reason", caption: "Reason from CRM", customizeText: default_to_nodata},
            {dataField: "majorSegmentShipTo", caption: "MajorSegment ShipTo"},
            {dataField: "globalIndustrySegment", caption: "Global Industry Segment by SIC4"},
            {dataField: "globalIndustrySegmentAPIS", caption: "Global Industry Segment by APIndSegmentShpTo"},
            {dataField: "prodHierLevel3", caption: "ProdHierLevel3"},
            {dataField: "apSiteName", caption: "Closest AP ASU"},
            {dataField: "aPmiles", caption: "AP miles", format: roundToWholeNumber, formatString: "roundToWholeNumber"},
            {dataField: "plantCoSite", caption: "Closest nonAP ASU"},
            {dataField: "nonAPmiles", caption: "nonAP miles", format: roundToWholeNumber, formatString: "roundToWholeNumber"},
            {dataField: "apAdv", caption: "AP mile Advantage", format: roundToWholeNumber, formatString: "roundToWholeNumber"},           

        ]
    },
    {
        datagridName: "CurrCust_LBSAP",
        staticColumnDefs: [
            {dataField: "territory", caption: "Territory by Zip"},
            {dataField: "shipToPartyId", caption: "ShipToID"},
            {dataField: "priSalesPosCRM", caption: "PrimarySalesPos CRM"},
            {dataField: "shipToPrimarySalesPosRepClassification", caption: "Sales Rep Classification"},
            {dataField: "bemfabDesc", caption: "BEMFAB from D&B"},                                    
            {dataField: "dunsNumber", caption: "DUNS Number"},
            {dataField: "shipToParty", caption: "Ship To Party"},
            {dataField: "streetName", caption: "Street"},
            {dataField: "locationCity", caption: "City"},
            {dataField: "countyPrefecture", caption: "County"},
            {dataField: "province", caption: "State"},
            {dataField: "postalCode", caption: "Postal Code"},
            {dataField: "isCryoEase", caption: "is CryoEase"},
            {dataField: "contractEndDate", caption: "Contract End Date", customizeText: get_date_from_isoString},
            {dataField: "sapName3", caption: "SAPName3 (tank model)"},                                    
            {dataField: "lastOrderMonth", caption: "Last Order", customizeText: get_yrmonth_from_isostring},
            {dataField: "reason", caption: "Reason from CRM", customizeText: default_to_nodata},
            {dataField: "competitor", caption: "Competitor"},
            {dataField: "majorSegmentShipTo", caption: "MajorSegment ShipTo"},
            {dataField: "globalIndustrySegment", caption: "Global Industry Segment by SIC4"},
            {dataField: "globalIndustrySegmentAPIS", caption: "Global Industry Segment by APIndSegmentShpTo"},            
            {dataField: "apSiteName", caption: "Closest AP ASU"},
            {dataField: "aPmiles", caption: "AP miles", format: roundToWholeNumber, formatString: "roundToWholeNumber"},
            {dataField: "plantCoSite", caption: "Closest nonAP ASU"},
            {dataField: "nonAPmiles", caption: "nonAP miles", format: roundToWholeNumber, formatString: "roundToWholeNumber"},
            {dataField: "apAdv", caption: "AP mile Advantage", format: roundToWholeNumber, formatString: "roundToWholeNumber"},
            {dataField: "grandTotDollarsPerYr", caption: "Total Sales $/yr", format: enUS_currencyRounded, formatString: "enUS_currencyRounded"},            
        ],
        dynamicColsFunc: getdynamicColumns_LBSAP,
        customizeColsFunc: customizeBands_LBSAP
    },
    {
        datagridName: "LeadsWith91MA_AllLeadsDetails",
        staticColumnDefs: [
            {dataField: "leadID", caption: "Lead ID", encodeHtml: false, customizeText: generate_leadID_hyperlink},            
            {dataField: "leadStatus", caption: "Status"},
            {dataField: "accountNum", caption: "Account Num", encodeHtml: false, customizeText: generate_acctnum_hyperlink},
            {dataField: "subRegion", caption: "SubRegion"},
            {dataField: "territory", caption: "Terrtry by Zip"},
            {dataField: "territoryCRM", caption: "Terrtry CRM"},                                    
            {dataField: "dunsNumber", caption: "DUNS Number"},
            {dataField: "company", caption: "Company"},
            {dataField: "streeT60", caption: "Street"},
            {dataField: "citY_1", caption: "City"},            
            {dataField: "region", caption: "State/Prov"},
            {dataField: "postalcode", caption: "Post Code"},
            {dataField: "country", caption: "Country"},
            {dataField: "phone", caption: "Phone"},
            {dataField: "contact", caption: "Contact"},
            {dataField: "crM_DESCR", caption: "Lead Description"},
            {dataField: "campaign", caption: "Campaign"},
            {dataField: "leadQualificationLevel", caption: "Qualification Level"},
            {dataField: "leadReason", caption: "Reason"},
            {dataField: "oppStatusOfWonLead", caption: "Oppty Status of Won Lead"},
            {dataField: "leadSince", caption: "Since"},
            {dataField: "fy", caption: "FY"},
            {dataField: "quarter", caption: "FYQ"},
            {dataField: "monthNumber", caption: "Mo.#"},
            {dataField: "monthName", caption: "Month"},
            {dataField: "leadPriority", caption: "Priority"},
            {dataField: "noOfCallAttempts", caption: "No Of Call Attempts"},
            {dataField: "leadGroup", caption: "Group"},
            {dataField: "product", caption: "Product"},
            {dataField: "apContact", caption: "APContact"},
            {dataField: "application1", caption: "Application1"},
            {dataField: "maMarketSegment", caption: "ma Market Segment"},
            {dataField: "majorSegment", caption: "Major Segment"},
            {dataField: "apIndustrySegment", caption: "APIndustry Segment"},
            {dataField: "companyInactiveCode", caption: "Company Inactive Code"},
            {dataField: "isLBUser", caption: "is LBUser"},
            {dataField: "isPGUser", caption: "is PGUser"},
            {dataField: "apSiteName", caption: "Closest AP ASU"},
            {dataField: "apMiles", caption: "Closest AP miles"},
            {dataField: "apSiteNameReal", caption: "Closest Running AP ASU"},
            {dataField: "apMilesReal", caption: "Closest Running AP miles"},
            {dataField: "nonCompany", caption: "Closest nonAP ASU"},
            {dataField: "nonSiteName", caption: "Closest nonAP ASU Site"},
            {dataField: "nonmiles", caption: "Closest nonAP miles"},
            {dataField: "nonCompany2", caption: "2nd Closest nonAP ASU"},
            {dataField: "nonSiteName2", caption: "2nd Closest nonAP ASU Site"},
            {dataField: "nonmiles2", caption: "2nd Closest nonAP miles"},
            {dataField: "apex", caption: "APEX"},
            {dataField: "cryoEaseMicrobulk", caption: "Cryo Ease Microbulk"},
            {dataField: "genGas", caption: "Gen Gas"},
            {dataField: "liquidBulk", caption: "Liquid Bulk"},
            {dataField: "packagedGas", caption: "Packaged Gas"},
            {dataField: "arContExpDate", caption: "Ar Contract Expiration Date"},
            {dataField: "arCurrSupp", caption: "Ar Current Supplier"},
            {dataField: "arCurrSupp2", caption: "Ar Current Supplier2"},
            {dataField: "arCustOwnTank", caption: "Ar Customer Owned Tank"},
            {dataField: "arLBU", caption: "Ar LBU"},
            {dataField: "arModeSupp", caption: "Ar Mode of Supply"},
            {dataField: "arModeSupp2", caption: "Ar Mode of Supply2"},
            {dataField: "arTankSize", caption: "Ar Tank Size"},
            {dataField: "arVol", caption: "Ar Volume"},
            {dataField: "arVol2", caption: "Ar Volume2"},
            {dataField: "arArgon", caption: "Ar Argon"},
            {dataField: "n2ContExpDate", caption: "N2 Contract Expiration Date"},
            {dataField: "n2CurrSupp", caption: "N2 Current Supplier"},
            {dataField: "n2CurrSupp2", caption: "N2 Current Supplier2"},
            {dataField: "n2CustOwnTank", caption: "N2 Customer Owned Tank"},
            {dataField: "n2LBU", caption: "N2 LBU"},
            {dataField: "n2ModeSupp", caption: "N2 Mode of Supply"},
            {dataField: "n2ModeSupp2", caption: "N2 Mode of Supply2"},
            {dataField: "n2ModeSupp3", caption: "N2 Mode of Supply3"},
            {dataField: "n2Price", caption: "N2 Price"},
            {dataField: "n2TankSize", caption: "N2 Tank Size"},
            {dataField: "n2TankSize2", caption: "N2 Tank Size2"},
            {dataField: "n2TankSize3", caption: "N2 Tank Size3"},
            {dataField: "n2Vol", caption: "N2 Volume"},
            {dataField: "n2Vol2", caption: "N2 Volume2"},
            {dataField: "n2Vol3", caption: "N2 Volume3"},
            {dataField: "n2Nitrogen", caption: "N2 Nitrogen"},
            {dataField: "o2ContExpDate", caption: "O2 Contract Expiration Date"},
            {dataField: "o2CurrSupp", caption: "O2 Current Supplier"},
            {dataField: "o2CurrSupp2", caption: "O2 Current Supplier2"},
            {dataField: "o2CustOwnTank", caption: "O2 Customer Owned Tank"},
            {dataField: "o2GenGasEquip", caption: "O2 Gen Gas Equipment"},
            {dataField: "o2LBU", caption: "O2 LBU"},
            {dataField: "o2ModeSupp", caption: "O2 Mode of Supply"},
            {dataField: "o2ModeSupp2", caption: "O2 Mode of Supply2"},
            {dataField: "o2ModeSupp3", caption: "O2 Mode of Supply3"},
            {dataField: "o2TankSize", caption: "O2 Tank Size"},
            {dataField: "o2TankSize2", caption: "O2 Tank Size2"},
            {dataField: "o2Vol", caption: "O2 Volume"},
            {dataField: "o2Vol2", caption: "O2 Volume2"},
            {dataField: "o2Vol3", caption: "O2 Volume3"},
            {dataField: "o2Oxygen", caption: "O2 Oxygen"},
            {dataField: "cO2ContExpDate", caption: "CO2 Contract Expiration Date"},
            {dataField: "cO2CurrSupp", caption: "CO2 Current Supplier"},
            {dataField: "cO2CustOwnTank", caption: "CO2 Customer Owned Tank"},
            {dataField: "cO2ModeSupp", caption: "CO2 Mode of Supply"},
            {dataField: "cO2ModeSupp2", caption: "CO2 Mode of Supply2"},
            {dataField: "cO2TankSize", caption: "CO2 Tank Size"},
            {dataField: "cO2Vol", caption: "CO2 Volume"},
            {dataField: "cO2CarbonDioxide", caption: "CO2 Carbon Dioxide"},
            {dataField: "h2ContExpDate", caption: "H2 Contract Expiration"},
            {dataField: "h2CurrSupp", caption: "H2 Current Supplier"},
            {dataField: "h2CustOwnTank", caption: "H2 Customer Owned Tank"},
            {dataField: "h2ModeSupp", caption: "H2 Mode of Supply"},
            {dataField: "h2ModeSupp2", caption: "H2 Mode of Supply2"},
            {dataField: "h2TankSize", caption: "H2 Tank Size"},
            {dataField: "h2Vol", caption: "H2 Volume"},
            {dataField: "h2Vol2", caption: "H2 Volume2"},
            {dataField: "h2Vol3", caption: "H2 Volume3"},
            {dataField: "h2Hydrogen", caption: "H2 Hydrogen"},
            {dataField: "heContExpDate", caption: "He Contract Expiration Date"},
            {dataField: "heCurrSupp", caption: "He Current Supplier"},
            {dataField: "heCustOwnTank", caption: "He Customer Owned Tank"},
            {dataField: "heModeSupp", caption: "He Mode of Supply"},
            {dataField: "heModeSupp2", caption: "He Mode of Supply2"},
            {dataField: "heTankSize", caption: "He Tank Size"},
            {dataField: "heVol", caption: "He Volume"},
            {dataField: "heVol2", caption: "He Volume2"},
            {dataField: "heHelium", caption: "He Helium"},
            {dataField: "pgContExpDate", caption: "PG Contract Expire Date"},
            {dataField: "pgProducts", caption: "PG Products"},
            {dataField: "pgProducts2", caption: "PG Products2"},
            {dataField: "pgProducts3", caption: "PG Products3"},
            {dataField: "pgCurrSupp", caption: "PG Products Current Supplier"},
            {dataField: "pgModeSupp", caption: "PG Products Mode of Supply"},
            {dataField: "pgVol", caption: "PG Products Volume"},
            {dataField: "compInactiveCd", caption: "Company Inactive Code"},
            {dataField: "mktSegment", caption: "Market Segment"},
            {dataField: "mktSegment2", caption: "Market Segment2"},
            {dataField: "mktSegment3", caption: "Market Segment3"},
            {dataField: "prodSite", caption: "Production Site "},
            {dataField: "expanPlans", caption: "Expansion Plans"},
            {dataField: "expanPlanYr", caption: "Expansion Plan Year"},
            {dataField: "noOfEmp", caption: "Number of Employees"},
            {dataField: "focusedAcct", caption: "Focused Account"},
            {dataField: "keyAcct", caption: "Key Account"},
        ]
    },
    {
        datagridName: "CRM_Prospects_With91MA",
        staticColumnDefs: [
            {dataField: "accountID", caption: "Account ID", encodeHtml: false, customizeText: generate_acctnum_hyperlink},
            {dataField: "territoryTCode", caption: "T Terr"},
            {dataField: "territoryMCode", caption: "M Terr"},
            {dataField: "company", caption: "Company"},
            {dataField: "namE_ORG2", caption: "NAME ORG2"},
            {dataField: "street", caption: "Street"},
            {dataField: "poBox", caption: "POBox"},
            {dataField: "city", caption: "City"},
            {dataField: "citY_2", caption: "CITY 2"},
            {dataField: "countyPrefecture", caption: "County"},
            {dataField: "stateProvince", caption: "State"},
            {dataField: "postCode", caption: "Post Code"},
            {dataField: "country", caption: "COUNTRY"},
            {dataField: "phone", caption: "phone"},
            {dataField: "lifeCycleStage", caption: "Life Cycle Stage"},
            {dataField: "bpRole", caption: "BPRole"},
            {dataField: "rentedAddress", caption: "Rented Address"},
            {dataField: "deL_INDIC", caption: "DEL INDIC"},            
            {dataField: "majorSegment", caption: "Major Segment"},
            {dataField: "apIndustrySegment", caption: "APIndustry Segment"},
            {dataField: "companyInactiveCode", caption: "Company Inactive Code"},
            {dataField: "isLBUser", caption: "is LBUser"},
            {dataField: "isPGUser", caption: "is PGUser"},
            {dataField: "application1", caption: "Application1"},
            {dataField: "subRegion", caption: "SubRegion"},
            {dataField: "createdon", caption: "CREATED ON"},
            {dataField: "apSiteName", caption: "Closest AP Site"},
            {dataField: "apMiles", caption: "Closest AP miles"},
            {dataField: "nonCompany", caption: "Closest nonAP Company"},
            {dataField: "nonSiteName", caption: "Closest nonAP Site"},
            {dataField: "nonmiles", caption: "Closest nonAP mi."},
            {dataField: "apSiteNameReal", caption: "Closest AP running Site"},
            {dataField: "apMilesReal", caption: "Closest AP running mi."},
            {dataField: "nonCompany2", caption: "2nd Closest nonAP Co."},
            {dataField: "nonSiteName2", caption: "2nd Closest nonAP Site"},
            {dataField: "nonmiles2", caption: "2nd Closest nonAP mi."},
            {dataField: "dunsNumber", caption: "DUNS Number"},
            {dataField: "openLead", caption: "Has Open Lead?"},
            {dataField: "openOppty", caption: "Has Open Oppty?"},
            {dataField: "apex", caption: "APEX"},
            {dataField: "cryoEaseMicrobulk", caption: "Cryo Ease Microbulk"},
            {dataField: "genGas", caption: "Gen Gas"},
            {dataField: "liquidBulk", caption: "Liquid Bulk"},
            {dataField: "packagedGas", caption: "Packaged Gas"},
            {dataField: "arContExpDate", caption: "Ar Contract Expiration Date"},
            {dataField: "arCurrSupp", caption: "Ar Current Supplier"},
            {dataField: "arCurrSupp2", caption: "Ar Current Supplier2"},
            {dataField: "arCustOwnTank", caption: "Ar Customer Owned Tank"},
            {dataField: "arLBU", caption: "Ar LBU"},
            {dataField: "arModeSupp", caption: "Ar Mode of Supply"},
            {dataField: "arModeSupp2", caption: "Ar Mode of Supply2"},
            {dataField: "arTankSize", caption: "Ar Tank Size"},
            {dataField: "arVol", caption: "Ar Volume"},
            {dataField: "arVol2", caption: "Ar Volume2"},
            {dataField: "arArgon", caption: "Ar Argon"},
            {dataField: "n2ContExpDate", caption: "N2 Contract Expiration Date"},
            {dataField: "n2CurrSupp", caption: "N2 Current Supplier"},
            {dataField: "n2CurrSupp2", caption: "N2 Current Supplier2"},
            {dataField: "n2CustOwnTank", caption: "N2 Customer Owned Tank"},
            {dataField: "n2LBU", caption: "N2 LBU"},
            {dataField: "n2ModeSupp", caption: "N2 Mode of Supply"},
            {dataField: "n2ModeSupp2", caption: "N2 Mode of Supply2"},
            {dataField: "n2ModeSupp3", caption: "N2 Mode of Supply3"},
            {dataField: "n2Price", caption: "N2 Price"},
            {dataField: "n2TankSize", caption: "N2 Tank Size"},
            {dataField: "n2TankSize2", caption: "N2 Tank Size2"},
            {dataField: "n2TankSize3", caption: "N2 Tank Size3"},
            {dataField: "n2Vol", caption: "N2 Volume"},
            {dataField: "n2Vol2", caption: "N2 Volume2"},
            {dataField: "n2Vol3", caption: "N2 Volume3"},
            {dataField: "n2Nitrogen", caption: "N2 Nitrogen"},
            {dataField: "o2ContExpDate", caption: "O2 Contract Expiration Date"},
            {dataField: "o2CurrSupp", caption: "O2 Current Supplier"},
            {dataField: "o2CurrSupp2", caption: "O2 Current Supplier2"},
            {dataField: "o2CustOwnTank", caption: "O2 Customer Owned Tank"},
            {dataField: "o2GenGasEquip", caption: "O2 Gen Gas Equipment"},
            {dataField: "o2LBU", caption: "O2 LBU"},
            {dataField: "o2ModeSupp", caption: "O2 Mode of Supply"},
            {dataField: "o2ModeSupp2", caption: "O2 Mode of Supply2"},
            {dataField: "o2ModeSupp3", caption: "O2 Mode of Supply3"},
            {dataField: "o2TankSize", caption: "O2 Tank Size"},
            {dataField: "o2TankSize2", caption: "O2 Tank Size2"},
            {dataField: "o2Vol", caption: "O2 Volume"},
            {dataField: "o2Vol2", caption: "O2 Volume2"},
            {dataField: "o2Vol3", caption: "O2 Volume3"},
            {dataField: "o2Oxygen", caption: "O2 Oxygen"},
            {dataField: "cO2ContExpDate", caption: "CO2 Contract Expiration Date"},
            {dataField: "cO2CurrSupp", caption: "CO2 Current Supplier"},
            {dataField: "cO2CustOwnTank", caption: "CO2 Customer Owned Tank"},
            {dataField: "cO2ModeSupp", caption: "CO2 Mode of Supply"},
            {dataField: "cO2ModeSupp2", caption: "CO2 Mode of Supply2"},
            {dataField: "cO2TankSize", caption: "CO2 Tank Size"},
            {dataField: "cO2Vol", caption: "CO2 Volume"},
            {dataField: "cO2CarbonDioxide", caption: "CO2 Carbon Dioxide"},
            {dataField: "h2ContExpDate", caption: "H2 Contract Expiration"},
            {dataField: "h2CurrSupp", caption: "H2 Current Supplier"},
            {dataField: "h2CustOwnTank", caption: "H2 Customer Owned Tank"},
            {dataField: "h2ModeSupp", caption: "H2 Mode of Supply"},
            {dataField: "h2ModeSupp2", caption: "H2 Mode of Supply2"},
            {dataField: "h2TankSize", caption: "H2 Tank Size"},
            {dataField: "h2Vol", caption: "H2 Volume"},
            {dataField: "h2Vol2", caption: "H2 Volume2"},
            {dataField: "h2Vol3", caption: "H2 Volume3"},
            {dataField: "h2Hydrogen", caption: "H2 Hydrogen"},
            {dataField: "heContExpDate", caption: "He Contract Expiration Date"},
            {dataField: "heCurrSupp", caption: "He Current Supplier"},
            {dataField: "heCustOwnTank", caption: "He Customer Owned Tank"},
            {dataField: "heModeSupp", caption: "He Mode of Supply"},
            {dataField: "heModeSupp2", caption: "He Mode of Supply2"},
            {dataField: "heTankSize", caption: "He Tank Size"},
            {dataField: "heVol", caption: "He Volume"},
            {dataField: "heVol2", caption: "He Volume2"},
            {dataField: "heHelium", caption: "He Helium"},
            {dataField: "pgContExpDate", caption: "PG Contract Expire Date"},
            {dataField: "pgProducts", caption: "PG Products"},
            {dataField: "pgProducts2", caption: "PG Products2"},
            {dataField: "pgProducts3", caption: "PG Products3"},
            {dataField: "pgCurrSupp", caption: "PG Products Current Supplier"},
            {dataField: "pgModeSupp", caption: "PG Products Mode of Supply"},
            {dataField: "pgVol", caption: "PG Products Volume"},
            {dataField: "compInactiveCd", caption: "Company Inactive Code"},
            {dataField: "mktSegment", caption: "Market Segment"},
            {dataField: "mktSegment2", caption: "Market Segment2"},
            {dataField: "mktSegment3", caption: "Market Segment3"},
            {dataField: "prodSite", caption: "Production Site "},
            {dataField: "expanPlans", caption: "Expansion Plans"},
            {dataField: "expanPlanYr", caption: "Expansion Plan Year"},
            {dataField: "noOfEmp", caption: "Number of Employees"},
            {dataField: "focusedAcct", caption: "Focused Account"},
            {dataField: "keyAcct", caption: "Key Account"},                       
        ]
    },
    {
        datagridName: "CRM_Suspects",
        staticColumnDefs: [
            {dataField: "territory", caption: "Territory"},
            {dataField: "dunsNumber", caption: "DUNS Number"},
            {dataField: "company", caption: "Company"},
            {dataField: "address", caption: "Address"},
            {dataField: "city", caption: "City"},
            {dataField: "countyPrefecture", caption: "County Prefecture"},
            {dataField: "stateProvince", caption: "State Province"},
            {dataField: "zipCode", caption: "Post Code"},
            {dataField: "country", caption: "Country"},
            {dataField: "phoneNumber", caption: "Phone Number"},
            {dataField: "apSiteName", caption: "Closest AP Plant"},
            {dataField: "apMiles", caption: "Closest AP Miles"},
            {dataField: "apSiteNameReal", caption: "Closest AP Running Plant"},
            {dataField: "apMilesReal", caption: "Closest AP Running  Miles"},
            {dataField: "nonCompany", caption: "Closest NonAP Co."},
            {dataField: "nonSiteName", caption: "Closest NonAP Site"},
            {dataField: "nonmiles", caption: "Closest NonAP Miles"},
            {dataField: "nonCompany2", caption: "2nd Closest NonAP Co."},
            {dataField: "nonSiteName2", caption: "2nd Closest NonAP Site"},
            {dataField: "nonmiles2", caption: "2nd Closest NonAP Miles"},
            {dataField: "exclude", caption: "Exclude", customizeText: boolToStringUpper, dataType: "string"},
            {dataField: "excludeComment", caption: "Exclude Comment"},
            {dataField: "custComment", caption: "Cust Comment"},
            {dataField: "locationType", caption: "Location Type"},
            {dataField: "siteRevenueDollars", caption: "Site Revenue $", format: enUS_roundToWholeNumber, formatString: "enUS_roundToWholeNumber"},
            {dataField: "isSiteRevenueOver1MM", caption: "is Site Revenue Over $1MM"},
            {dataField: "employeeCountTotal", caption: "Employee Count Total"},
            {dataField: "employeeCountHere", caption: "Employee Count Here"},
            {dataField: "isSmallBusiness", caption: "is Small Business", customizeText: boolToStringUpper, dataType: "string"},
            {dataField: "squareFootage", caption: "Square Footage"},
            {dataField: "yearStarted", caption: "Year Started"},
            {dataField: "lineOfBusinessPerDandB", caption: "Line Of Business Per D&B"},
            {dataField: "isGasUser", caption: "is Gas User", customizeText: yesNoValues_resetNoToEmpty},
            {dataField: "isAPAccount", caption: "is AP Account", customizeText: yesNoValues_resetNoToEmpty},
            {dataField: "isCompetitorAccount", caption: "is Competitor Account", customizeText: yesNoValues_resetNoToEmpty},
            {dataField: "competitorName", caption: "Competitor Name"},
            {dataField: "globalIndustrySegment", caption: "Global Industry Segment"},
            {dataField: "siC2Desc", caption: "SIC2 Desc"},
            {dataField: "siC8", caption: "SIC8"},
            {dataField: "siC8Desc", caption: "SIC8 Desc"},
            {dataField: "naicsCode", caption: "NAICS Code"},
            {dataField: "naicsCodeDescription", caption: "NAICS  Description"},
            {dataField: "ynProbability", caption: "Yes/No Probability"},
            {dataField: "n2MTD", caption: "Relative  LIN Volume by 'mva' ", format: enUS_roundToTwoDecimals, formatString: "enUS_roundToTwoDecimals"},
            {dataField: "o2MTD", caption: "Relative  LOX Volume by 'mva' ", format: enUS_roundToTwoDecimals, formatString: "enUS_roundToTwoDecimals"},
            {dataField: "arMTD", caption: "Relative  LAR Volume by 'mva' ", format: enUS_roundToTwoDecimals, formatString: "enUS_roundToTwoDecimals"},
            {dataField: "siteID", caption: "SiteID"},
        ]
    },
    {
        datagridName: "OpptysWith91MA_AllOpptysDetails",
        staticColumnDefs: [
            {dataField: "salesStage", caption: "Sales Stage"},
            {dataField: "salesStagePct", caption: "%"},
            {dataField: "oppStatus", caption: "Status"},
            {dataField: "opportunityID", caption: "Oppty ID", encodeHtml: false, customizeText: generate_OpptyID_hyperlink},
            {dataField: "accountNum", caption: "Account Num", encodeHtml: false, customizeText: generate_acctnum_hyperlink},
            {dataField: "subRegion", caption: "SubRegion"},
            {dataField: "territory", caption: "Terrtry by Zip"},
            {dataField: "crmTerritory", caption: "Terrtry CRM"},
            {dataField: "lbu", caption: "LBU"},
            {dataField: "isFAMKAM", caption: "is FAM/KAM"},
            {dataField: "dunsNumber", caption: "DUNS Number"},
            {dataField: "company", caption: "Company"},
            {dataField: "streeT60", caption: "Street"},
            {dataField: "citY_1", caption: "City"},
            {dataField: "countyPrefecture", caption: "County"},
            {dataField: "region", caption: "State Prov"},
            {dataField: "postalcode", caption: "Post Code"},
            {dataField: "country", caption: "Country"},
            {dataField: "opType", caption: "Oppty Type"},
            {dataField: "businessType", caption: "Business Type"},
            {dataField: "opportunityType", caption: "Opportunity Type"},
            {dataField: "oppPriority", caption: "Sales Priority"},
            {dataField: "competitor", caption: "Competitor"},
            {dataField: "supplierName", caption: "ma Current Supplier"},
            {dataField: "reason", caption: "Reason"},
            {dataField: "teamLeadSource", caption: "Lead Source"},
            {dataField: "campaign", caption: "Campaign"},
            {dataField: "createdDate", caption: "Created Date"},
            {dataField: "fy", caption: "FY created"},
            {dataField: "quarter", caption: "FYQ created"},
            {dataField: "monthNumber", caption: "Mo. # cr"},
            {dataField: "monthName", caption: "Month cr"},
            {dataField: "closingDate", caption: "Closing Date"},
            {dataField: "fYcl", caption: "FY closing"},
            {dataField: "qtrcl", caption: "Qtr closing"},
            {dataField: "modifiedDate", caption: "Modified On"},
            {dataField: "createdBy", caption: "Created By"},
            {dataField: "modifiedDateItem", caption: "Modified On Item"},
            {dataField: "createdByItem", caption: "Created By Item"},
            {dataField: "apTechnologyOffering", caption: "APTechnology Offering"},
            {dataField: "application1", caption: "Application1"},
            {dataField: "maMarketSegment", caption: "ma Market Segment"},
            {dataField: "majorSegment", caption: "Major Segment"},
            {dataField: "apIndustrySegment", caption: "APIndustry Segment"},
            {dataField: "itemNum", caption: "Item#"},
            {dataField: "product", caption: "Product"},
            {dataField: "unitPrice", caption: "Unit Price"},
            {dataField: "annualQuantity", caption: "Annual Quantity"},
            {dataField: "qtyUnit", caption: "Qty Unit"},
            {dataField: "mtd", caption: "MTD"},
            {dataField: "expTotalValue", caption: "Exp Total Value"},
            {dataField: "contractExpireDate", caption: "Contract Expires"},
            {dataField: "teL_NUMDIS", caption: "Phone"},
            {dataField: "contact", caption: "Contact"},
            {dataField: "apContact", caption: "AP Contact"},
            {dataField: "companyInactiveCode", caption: "Company Inactive Code"},
            {dataField: "isLBUser", caption: "is LBUser"},
            {dataField: "isPGUser", caption: "is PGUser"},
            {dataField: "apSiteName", caption: "Closest AP ASU"},
            {dataField: "apMiles", caption: "Closest AP miles"},
            {dataField: "apSiteNameReal", caption: "Closest Running AP ASU"},
            {dataField: "apMilesReal", caption: "Closest Running AP miles"},
            {dataField: "nonCompany", caption: "Closest nonAP ASU"},
            {dataField: "nonSiteName", caption: "Closest nonAP ASU Site"},
            {dataField: "nonmiles", caption: "Closest nonAP miles"},
            {dataField: "nonCompany2", caption: "2nd Closest nonAP ASU"},
            {dataField: "nonSiteName2", caption: "2nd Closest nonAP ASU Site"},
            {dataField: "nonmiles2", caption: "2nd Closest nonAP miles"},
            {dataField: "apex", caption: "APEX"},
            {dataField: "cryoEaseMicrobulk", caption: "Cryo Ease Microbulk"},
            {dataField: "genGas", caption: "Gen Gas"},
            {dataField: "liquidBulk", caption: "Liquid Bulk"},
            {dataField: "packagedGas", caption: "Packaged Gas"},
            {dataField: "arContExpDate", caption: "Ar Contract Expiration Date"},
            {dataField: "arCurrSupp", caption: "Ar Current Supplier"},
            {dataField: "arCurrSupp2", caption: "Ar Current Supplier2"},
            {dataField: "arCustOwnTank", caption: "Ar Customer Owned Tank"},
            {dataField: "arLBU", caption: "Ar LBU"},
            {dataField: "arModeSupp", caption: "Ar Mode of Supply"},
            {dataField: "arModeSupp2", caption: "Ar Mode of Supply2"},
            {dataField: "arTankSize", caption: "Ar Tank Size"},
            {dataField: "arVol", caption: "Ar Volume"},
            {dataField: "arVol2", caption: "Ar Volume2"},
            {dataField: "arArgon", caption: "Ar Argon"},
            {dataField: "n2ContExpDate", caption: "N2 Contract Expiration Date"},
            {dataField: "n2CurrSupp", caption: "N2 Current Supplier"},
            {dataField: "n2CurrSupp2", caption: "N2 Current Supplier2"},
            {dataField: "n2CustOwnTank", caption: "N2 Customer Owned Tank"},
            {dataField: "n2LBU", caption: "N2 LBU"},
            {dataField: "n2ModeSupp", caption: "N2 Mode of Supply"},
            {dataField: "n2ModeSupp2", caption: "N2 Mode of Supply2"},
            {dataField: "n2ModeSupp3", caption: "N2 Mode of Supply3"},
            {dataField: "n2Price", caption: "N2 Price"},
            {dataField: "n2TankSize", caption: "N2 Tank Size"},
            {dataField: "n2TankSize2", caption: "N2 Tank Size2"},
            {dataField: "n2TankSize3", caption: "N2 Tank Size3"},
            {dataField: "n2Vol", caption: "N2 Volume"},
            {dataField: "n2Vol2", caption: "N2 Volume2"},
            {dataField: "n2Vol3", caption: "N2 Volume3"},
            {dataField: "n2Nitrogen", caption: "N2 Nitrogen"},
            {dataField: "o2ContExpDate", caption: "O2 Contract Expiration Date"},
            {dataField: "o2CurrSupp", caption: "O2 Current Supplier"},
            {dataField: "o2CurrSupp2", caption: "O2 Current Supplier2"},
            {dataField: "o2CustOwnTank", caption: "O2 Customer Owned Tank"},
            {dataField: "o2GenGasEquip", caption: "O2 Gen Gas Equipment"},
            {dataField: "o2LBU", caption: "O2 LBU"},
            {dataField: "o2ModeSupp", caption: "O2 Mode of Supply"},
            {dataField: "o2ModeSupp2", caption: "O2 Mode of Supply2"},
            {dataField: "o2ModeSupp3", caption: "O2 Mode of Supply3"},
            {dataField: "o2TankSize", caption: "O2 Tank Size"},
            {dataField: "o2TankSize2", caption: "O2 Tank Size2"},
            {dataField: "o2Vol", caption: "O2 Volume"},
            {dataField: "o2Vol2", caption: "O2 Volume2"},
            {dataField: "o2Vol3", caption: "O2 Volume3"},
            {dataField: "o2Oxygen", caption: "O2 Oxygen"},
            {dataField: "cO2ContExpDate", caption: "CO2 Contract Expiration Date"},
            {dataField: "cO2CurrSupp", caption: "CO2 Current Supplier"},
            {dataField: "cO2CustOwnTank", caption: "CO2 Customer Owned Tank"},
            {dataField: "cO2ModeSupp", caption: "CO2 Mode of Supply"},
            {dataField: "cO2ModeSupp2", caption: "CO2 Mode of Supply2"},
            {dataField: "cO2TankSize", caption: "CO2 Tank Size"},
            {dataField: "cO2Vol", caption: "CO2 Volume"},
            {dataField: "cO2CarbonDioxide", caption: "CO2 Carbon Dioxide"},
            {dataField: "h2ContExpDate", caption: "H2 Contract Expiration"},
            {dataField: "h2CurrSupp", caption: "H2 Current Supplier"},
            {dataField: "h2CustOwnTank", caption: "H2 Customer Owned Tank"},
            {dataField: "h2ModeSupp", caption: "H2 Mode of Supply"},
            {dataField: "h2ModeSupp2", caption: "H2 Mode of Supply2"},
            {dataField: "h2TankSize", caption: "H2 Tank Size"},
            {dataField: "h2Vol", caption: "H2 Volume"},
            {dataField: "h2Vol2", caption: "H2 Volume2"},
            {dataField: "h2Vol3", caption: "H2 Volume3"},
            {dataField: "h2Hydrogen", caption: "H2 Hydrogen"},
            {dataField: "heContExpDate", caption: "He Contract Expiration Date"},
            {dataField: "heCurrSupp", caption: "He Current Supplier"},
            {dataField: "heCustOwnTank", caption: "He Customer Owned Tank"},
            {dataField: "heModeSupp", caption: "He Mode of Supply"},
            {dataField: "heModeSupp2", caption: "He Mode of Supply2"},
            {dataField: "heTankSize", caption: "He Tank Size"},
            {dataField: "heVol", caption: "He Volume"},
            {dataField: "heVol2", caption: "He Volume2"},
            {dataField: "heHelium", caption: "He Helium"},
            {dataField: "pgContExpDate", caption: "PG Contract Expire Date"},
            {dataField: "pgProducts", caption: "PG Products"},
            {dataField: "pgProducts2", caption: "PG Products2"},
            {dataField: "pgProducts3", caption: "PG Products3"},
            {dataField: "pgCurrSupp", caption: "PG Products Current Supplier"},
            {dataField: "pgModeSupp", caption: "PG Products Mode of Supply"},
            {dataField: "pgVol", caption: "PG Products Volume"},
            {dataField: "compInactiveCd", caption: "Company Inactive Code"},
            {dataField: "mktSegment", caption: "Market Segment"},
            {dataField: "mktSegment2", caption: "Market Segment2"},
            {dataField: "mktSegment3", caption: "Market Segment3"},
            {dataField: "prodSite", caption: "Production Site "},
            {dataField: "expanPlans", caption: "Expansion Plans"},
            {dataField: "expanPlanYr", caption: "Expansion Plan Year"},
            {dataField: "noOfEmp", caption: "Number of Employees"},
            {dataField: "focusedAcct", caption: "Focused Account"},
            {dataField: "keyAcct", caption: "Key Account"},
        ]
    }
]


//********** END *******************

/*
D. DYNAMIC COLUMN GENERATING FUNCTIONS: 
*/

export function getdynamicColumns_LBSAP(gridData: Array<Object>, reportType: string) {

    let dynamicColumns = new Array<Column>();
    let groupingColumns = new Array<Column>();    

    let productLookupTable = new Array<string>();

    gridData.forEach((row,i) => {
        for(const product of row["prodHierLevel3_DetailsList"]){
            
            let productQtyUnit = product.prodHierLevel3 + '_' + product.qtyUnit;            

            let newfield_totDollars = "totDollarsPerYr" + '_' + productQtyUnit;
            let newfield_totCscfpd = "totCscfpd" + '_' + productQtyUnit;
            let newfield_totMTD = "totMTD" + '_' + productQtyUnit;

            gridData[i][newfield_totDollars] = product.totDollarsPerYr;
            gridData[i][newfield_totCscfpd] = product.totCscfpd;
            gridData[i][newfield_totMTD] = product.totMTD;

            if(productLookupTable.indexOf(productQtyUnit) == -1){
                dynamicColumns.push({dataField: newfield_totDollars, caption: "$/Yr", format: enUS_currencyRounded, formatString: "enUS_currencyRounded"});
                dynamicColumns.push({dataField: newfield_totCscfpd, caption: "scf/mo", format: enUS_roundToWholeNumber, formatString: "enUS_roundToWholeNumber"});
                dynamicColumns.push({dataField: newfield_totMTD, caption: "MTD", format: enUS_roundToTwoDecimals, formatString: "enUS_roundToTwoDecimals"});                                                  

                groupingColumns.push({caption: productQtyUnit, isBand: true, cssClass: "headerBandingCellClass"});

                productLookupTable.push(productQtyUnit);
            }

        }
    });

    let result = {
        dynamicColumns: dynamicColumns,
        groupingColumns: groupingColumns
    }

    if(groupingColumns.length > 0) {
        if(reportType == 'CurrCust_LBSAP'){
            groupingColumns_LBSAP = groupingColumns;
        }
    }

    return result;
}

//********** END *******************

/*
E. COLUMN GROUPING FUNCTIONS AKA COLUMN BANDING FUNCTIONS : 
*/

export var groupingColumns_LBSAP: Array<any> = new Array<any>();


export function customizeBands_LBSAP(columns: Array<any>){    

    columns.push(...groupingColumns_LBSAP);

    groupingColumns_LBSAP.forEach((row,i) => {
        columns.forEach((colDefn,j) => {
            
            if(colDefn.dataField && colDefn.dataField.substring(colDefn.dataField.indexOf('_') + 1) === row.caption){ 
                let bandColIdx = columns.findIndex(col => col.caption == row.caption);
                columns[j].ownerBand = bandColIdx;                
                columns[bandColIdx]["headerCellTemplate"] = "lbsap_product_headerTemplate";
            }
        });
    });

    
}

//********** END *******************

export function isDecompressRequired(searchReportType: string){
    return (requiresDecompression.findIndex(reportType => reportType == searchReportType) != -1)
}

